import { PROSPECT_SUBMISSION_STATUS } from '../../constants';
import { asId, matchesOpportunity } from '../ClientWorkspace/engageAvailability';

export const CONTROL_STATE = {
  DISABLED: 'disabled',
  ELIGIBLE: 'eligible',
};

export const NEW_COMMUNICATION_LIMIT = 25000;
export const MESSAGE_TEXT_LIMIT = 20000;
export const SPEAKER_NAME_LIMIT = 500;

export const EMPTY_RELATIONSHIP = {
  howDoYouKnowThisPerson: '',
  whyRelevant: '',
  anythingElse: '',
};

export const RELATIONSHIP_FIELDS = [
  {
    key: 'howDoYouKnowThisPerson',
    label: 'How do you know this person?',
    placeholder: 'Add details about your relationship...',
  },
  {
    key: 'whyRelevant',
    label: 'Why do you think this could be relevant?',
    placeholder: 'Explain why this prospect could be relevant...',
  },
  {
    key: 'anythingElse',
    label: 'Anything else I should know?',
    placeholder: 'Add any additional context...',
  },
];

export const RELATIONSHIP_TOOLTIP =
  'Changes here are used only for this Engage session and do not update the approved Prospect. To make permanent changes, edit the Prospect from the Prospects page.';

export const NEW_COMMUNICATION_TOOLTIP =
  'Paste anything new from the conversation here — a single message, several messages, or a full email/LinkedIn thread. Add it whenever useful, including multiple times during the same Engage session.\nInclude as much original context as you have, especially names, dates and times.\nNo need to clean, reorder or remove quoted/duplicate text first; TechXpander will reconcile it with the existing Conversation History.';

export const approvedProspectsForOpportunity = (prospects, clientId, programId) =>
  (prospects || []).filter(
    (row) => matchesOpportunity(row, clientId, programId) && row.status === PROSPECT_SUBMISSION_STATUS.APPROVED,
  );

export const resolveSelectedProspectId = (selectedId, approved) => {
  const rows = approved || [];
  const current = rows.find((row) => String(row._id) === String(selectedId || ''));
  if (current) return String(current._id);
  if (rows.length === 1) return String(rows[0]._id);
  return '';
};

export const relationshipSessionValues = (prospect) => ({
  howDoYouKnowThisPerson: prospect?.howDoYouKnowThisPerson || '',
  whyRelevant: prospect?.whyRelevant || '',
  anythingElse: prospect?.anythingElse || '',
});

export const isRelationshipDirty = (session, prospect) => {
  const original = relationshipSessionValues(prospect);
  const current = session || EMPTY_RELATIONSHIP;
  return RELATIONSHIP_FIELDS.some((field) => current[field.key] !== original[field.key]);
};

export const historySnapshot = (messages) =>
  (messages || []).map((message) => ({
    speakerName: String(message?.speakerName || '').trim() || null,
    speakerRole: message?.speakerRole || null,
    text: message?.text || '',
    occurredAt: message?.occurredAt || null,
  }));

export const isHistoryDirty = (current, saved) =>
  JSON.stringify(historySnapshot(current)) !== JSON.stringify(historySnapshot(saved));

export const hasNewCommunication = (value) => Boolean(String(value || '').trim());

export const hasUnsentMessage = (messageText, confirmedText) => {
  const text = String(messageText || '');
  if (!text.trim()) return false;
  return text !== String(confirmedText ?? '');
};

export const hasUnsavedEngageWork = ({
  relationship,
  prospect,
  history,
  savedHistory,
  newCommunication,
  messageText = '',
  confirmedText = null,
}) =>
  isRelationshipDirty(relationship, prospect) ||
  isHistoryDirty(history, savedHistory) ||
  hasNewCommunication(newCommunication) ||
  hasUnsentMessage(messageText, confirmedText);

export const selectProspectResult = ({
  nextId,
  selectedId,
  draft,
  prospect,
  historyDirty = false,
  newCommunication = '',
  messageUnsent = false,
}) => {
  const next = String(nextId || '');
  if (!next || next === String(selectedId || '')) return { type: 'ignore' };
  if (
    isRelationshipDirty(draft, prospect) ||
    historyDirty ||
    hasNewCommunication(newCommunication) ||
    messageUnsent
  ) {
    return { type: 'confirm', nextId: next };
  }
  return { type: 'switch', nextId: next };
};

export const historyLoadStatus = ({ prospectSelected, data, isError }) => {
  if (!prospectSelected) return 'idle';
  // A loaded document, including an empty history, is ready. A later refetch
  // error must not hide that and disable Generate message.
  if (data != null) return 'ready';
  if (isError) return 'error';
  return 'loading';
};

export const generateMessageControl = (prospectSelected, historyReady, hasGenerated = false, messageText = '') => {
  if (!prospectSelected || !historyReady) return CONTROL_STATE.DISABLED;
  if (hasGenerated && !String(messageText || '').trim()) return CONTROL_STATE.DISABLED;
  return CONTROL_STATE.ELIGIBLE;
};

// The reconcile request carries the writing action. A cleared ref must not
// drop generation after history has already been saved.
export const writingFollowUp = (pending, selectedProspectId) => {
  if (!pending?.mode) return null;
  if (String(pending.prospectId || '') !== String(selectedProspectId || '')) return null;
  return pending;
};

export const MANUAL_VERSION_TITLE = 'Manual edit';

export const QUICK_REFINE = [
  { label: 'Shorter', instruction: 'Make this shorter.' },
  { label: 'Warmer', instruction: 'Make this warmer.' },
  { label: 'More direct', instruction: 'Make this more direct.' },
  { label: 'More casual', instruction: 'Make this more casual.' },
];

export const GENERATE_INFO =
  'Uses the current Client, Product, Prospect and Conversation History to create the best next message for this relationship.';

export const GENERATE_ANOTHER_INFO =
  'Creates a meaningfully different alternative using the same current context, your current draft and the drafts created in this session.';

export const NEW_COMMUNICATION_HELP =
  'Paste anything new from the conversation here - a single message, several messages, or a full email/LinkedIn thread. Add it whenever useful, including multiple times during the same Engage session. Include as much original context as you have, especially names, dates and times. No need to clean, reorder or remove quoted/duplicate text first; TechXpander will reconcile it with the existing Conversation History.';

let versionSeq = 0;

export const captureManualVersion = (versions, messageText, baselineText) => {
  const text = String(messageText || '');
  const baseline = String(baselineText ?? '');
  const list = versions || [];
  if (text === baseline || !text.trim()) {
    return { versions: list, baselineText: baseline, captured: false };
  }
  if (list[0]?.message === text) {
    return { versions: list, baselineText: text, captured: false };
  }
  versionSeq += 1;
  return {
    versions: [
      {
        id: `manual-${versionSeq}`,
        title: MANUAL_VERSION_TITLE,
        message: text,
        source: 'manual',
        createdAt: new Date().toISOString(),
      },
      ...list,
    ],
    baselineText: text,
    captured: true,
  };
};

export const isNearFullCopy = (messageText, selectedText) => {
  const message = String(messageText || '');
  const selected = String(selectedText || '');
  if (!message.trim() || !selected.trim()) return false;
  if (selected.trim() === message.trim()) return true;
  return selected.length / message.length >= 0.9;
};

export const historyMessagesValid = (messages) =>
  (messages || []).every((message) => String(message?.text || '').trim());

export const refineMessageControl = (prospectSelected, messageText) => {
  if (!prospectSelected || !String(messageText || '').trim()) return CONTROL_STATE.DISABLED;
  return CONTROL_STATE.ELIGIBLE;
};

export const prospectOptionLabel = (prospect) =>
  `${prospect?.contactName || 'Prospect'} — ${prospect?.company || ''}`.trim();

export const opportunityLabels = (prospects, clientId, programId, companies, products) => {
  const rows = (prospects || []).filter((row) => matchesOpportunity(row, clientId, programId));
  const clientName =
    rows.find((row) => row.client?.companyName)?.client.companyName ||
    (companies || []).find((company) => asId(company.client) === String(clientId))?.companyName ||
    '';
  const productName =
    rows.find((row) => row.program?.productName)?.program.productName ||
    (products || []).find((product) => String(product._id) === String(programId))?.productName ||
    '';
  return { clientName, productName };
};

export const externalHref = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const HISTORY_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatHistoryDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  const day = date.getUTCDate();
  const month = HISTORY_MONTHS[date.getUTCMonth()];
  const time = `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
  return `${day} ${month} ${date.getUTCFullYear()}, ${time}`;
};

// Versions are created in this browser session, so show the user's local time.
export const formatVersionTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  return `${date.getDate()} ${HISTORY_MONTHS[date.getMonth()]}, ${time}`;
};

export const dateTimeParts = (value) => {
  if (!value) return { date: '', time: '' };
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return { date: '', time: '' };
  const pad = (part) => String(part).padStart(2, '0');
  return {
    date: `${parsed.getUTCFullYear()}-${pad(parsed.getUTCMonth() + 1)}-${pad(parsed.getUTCDate())}`,
    time: `${pad(parsed.getUTCHours())}:${pad(parsed.getUTCMinutes())}`,
  };
};

export const dateTimeToIso = (date, time) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  if (!match) return null;
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(time || '') || ['', '00', '00'];
  const parsed = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(timeMatch[1]), Number(timeMatch[2]), 0, 0),
  );
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

export const appLeaveHref = (link, currentHref) => {
  if (!link?.href || link.download) return '';
  if (link.target && link.target !== '_self') return '';
  let next;
  let current;
  try {
    current = new URL(currentHref);
    next = new URL(link.href, current.origin);
  } catch {
    return '';
  }
  if (next.origin !== current.origin) return '';
  if (next.pathname === current.pathname && next.search === current.search) return '';
  return `${next.pathname}${next.search}${next.hash}`;
};

export const speakerRoleLabel = (role) => {
  if (role === 'leadAgent') return 'Lead Agent';
  if (role === 'prospect') return 'Prospect';
  return '';
};

export const historyPayload = (data) => {
  if (Array.isArray(data)) return { revision: 0, messages: data };
  return {
    revision: Number.isInteger(data?.revision) ? data.revision : 0,
    messages: data?.messages || [],
  };
};

export const historyRevision = (data) => historyPayload(data).revision;

export const historyForProspect = (data, prospectId) =>
  historyPayload(data).messages.filter((item) => String(item?.prospectId) === String(prospectId || ''));

export const applyReconcileResult = ({ selectedProspectId, requestProspectId, data }) => {
  if (!data) return { type: 'ignore' };
  const sameProspect = String(selectedProspectId || '') === String(requestProspectId || '');
  if (data.status === 'warning') {
    return sameProspect ? { type: 'warning', proposalId: data.proposalId } : { type: 'ignore' };
  }
  return {
    type: 'applied',
    prospectId: requestProspectId,
    history: data.history || [],
    revision: Number.isInteger(data.revision) ? data.revision : 0,
    updateSession: sameProspect,
  };
};
